import { formatMoney } from '../lib/currency';
import { newId } from '../lib/defaults';
import {
  CONFERENCE_COST_CATEGORIES,
  type CostCategory,
  type CostCategoryDef,
  type CostItem,
  type EventData,
  type VatMode,
} from '../lib/types';
import NumberField from './NumberField';

type Props = {
  data: EventData;
  currency: string;
  vatMode: VatMode;
  vatRate: number;
  // Template-specific category list. Falls back to conference layout for
  // callers that don't pass anything.
  categories?: CostCategoryDef[];
  onChange: (patch: (d: EventData) => EventData) => void;
};

export default function CostsTab({
  data,
  currency,
  vatMode,
  vatRate,
  categories = CONFERENCE_COST_CATEGORIES,
  onChange,
}: Props) {
  const totals = categories.map((c) => ({
    key: c.key,
    label: c.label,
    sum: data.costs[c.key].reduce((a, x) => a + x.amount, 0),
  }));
  const grandTotalNet = totals.reduce((a, b) => a + b.sum, 0);
  const showVat = vatMode === 'gross';
  const grandTotalVat = showVat ? grandTotalNet * vatRate : 0;
  const grandTotalGross = grandTotalNet + grandTotalVat;
  const denom = showVat ? grandTotalGross : grandTotalNet;

  return (
    <div className="row-stack">
      <div className="card">
        <h2>
          Cost overview
          <span className="muted section-meta-tag">
            · {showVat ? `net + ${(vatRate * 100).toFixed(0)}% VAT` : 'net of VAT'}
          </span>
        </h2>
        <div className="breakdown">
          {totals.map((t) => {
            const vat = showVat ? t.sum * vatRate : 0;
            const gross = t.sum + vat;
            const netPct = denom > 0 ? (t.sum / denom) * 100 : 0;
            const vatPct = denom > 0 ? (vat / denom) * 100 : 0;
            const n = Math.max(0, Math.min(100, Math.round(netPct)));
            const v = Math.max(0, Math.min(100, Math.round(vatPct)));
            return (
              <div key={t.key} className="breakdown-row">
                <div className="top">
                  <span>{t.label}</span>
                  <span className="amt">
                    {showVat ? (
                      <>
                        <span className="muted">net</span> {formatMoney(t.sum, currency)}
                        <span className="muted"> · VAT</span> {formatMoney(vat, currency)}
                        <span className="muted"> · gross</span> {formatMoney(gross, currency)}
                      </>
                    ) : (
                      formatMoney(t.sum, currency)
                    )}
                  </span>
                </div>
                <div className={`bar costs ${showVat ? 'has-vat' : ''}`}>
                  <div className={`bar-net w-pct-${n}`} />
                  {showVat && <div className={`bar-vat w-pct-${v}`} />}
                </div>
              </div>
            );
          })}
        </div>
        {showVat ? (
          <div className="sum-bar sum-bar-stacked">
            <span>Total cost</span>
            <div className="sum-vat-breakdown">
              <span className="sum-net">Net {formatMoney(grandTotalNet, currency)}</span>
              <span className="sum-sep">·</span>
              <span className="sum-vat">VAT {formatMoney(grandTotalVat, currency)}</span>
              <span className="sum-sep">·</span>
              <span className="total">Gross {formatMoney(grandTotalGross, currency)}</span>
            </div>
          </div>
        ) : (
          <div className="sum-bar">
            <span>Total cost</span>
            <span className="total">{formatMoney(grandTotalNet, currency)}</span>
          </div>
        )}
      </div>

      <div className="card-grid">
        {categories.map((c) => (
          <CostBucket
            key={c.key}
            category={c.key}
            label={c.label}
            items={data.costs[c.key]}
            currency={currency}
            onChange={(items) =>
              onChange((d) => ({
                ...d,
                costs: { ...d.costs, [c.key]: items },
              }))
            }
          />
        ))}
      </div>
    </div>
  );
}

function CostBucket({
  category,
  label,
  items,
  currency,
  onChange,
}: {
  category: CostCategory;
  label: string;
  items: CostItem[];
  currency: string;
  onChange: (items: CostItem[]) => void;
}) {
  function patch(idx: number, p: Partial<CostItem>) {
    onChange(items.map((it, i) => (i === idx ? { ...it, ...p } : it)));
  }
  function add() {
    onChange([
      ...items,
      { id: newId(category), name: '', amount: 0 },
    ]);
  }
  function remove(idx: number) {
    onChange(items.filter((_, i) => i !== idx));
  }
  const total = items.reduce((a, x) => a + x.amount, 0);

  return (
    <div className="card">
      <div className="section-head">
        <h2>{label}</h2>
        <button className="btn ghost sm" onClick={add}>
          + Add line
        </button>
      </div>

      {items.length === 0 ? (
        <div className="muted">No items yet.</div>
      ) : (
        <div className="cost-list">
          {items.map((it, idx) => (
            <div key={it.id} className="cost-item">
              <input
                className="input"
                placeholder="Description"
                value={it.name}
                onChange={(e) => patch(idx, { name: e.target.value })}
              />
              <NumberField
                value={it.amount}
                onChange={(n) => patch(idx, { amount: n })}
              />
              <button
                className="btn sm danger"
                onClick={() => remove(idx)}
                title="Remove"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="sum-bar">
        <span>Σ {label}</span>
        <span className="total">{formatMoney(total, currency)}</span>
      </div>
    </div>
  );
}
